// src/components/ChatMessages.js
import React, { useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import TypingIndicator from "./TypingIndicator";
import NewsBuddyLogo from "../NewsAssistantLogo";
import { AssistantFeedback } from "./AssistantFeedback";

// Helper functions for favicon.
function getHostname(url) {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return null;
  }
}

function getFaviconUrl(url) {
  const hostname = getHostname(url);
  return hostname
    ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`
    : null;
}

function FaviconLink({
  href,
  refNumber,
  showPreview = false,
  children,
  className = "",
  ...props
}) {
  const [hasFavicon, setHasFavicon] = React.useState(true);
  const faviconUrl = getFaviconUrl(href);
  if (!faviconUrl) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        {...props}
      >
        {children}
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      {...props}
    >
      {hasFavicon && (
        <>
          <img
            src={faviconUrl}
            alt=""
            className="w-4 h-4 rounded-full"
            onError={() => setHasFavicon(false)}
          />
        </>
      )}
      {(!hasFavicon || showPreview) && <span>{children}</span>}
    </a>
  );
}

/**
 * Renders markdown content.
 * @param {string} content - Markdown text.
 * @param {function} [onBoldClick] - Optional callback triggered when bold text is tapped.
 */
function renderMarkdown(content, onBoldClick) {
  const references = new Map();
  let referenceCounter = 1;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Bold text renderer for interactive assistant messages.
        strong: ({ node, children, ...props }) => {
          if (onBoldClick) {
            return (
              <strong
                {...props}
                className="clickable-bold cursor-pointer hover:underline active:scale-95 transition-transform"
                onClick={() => onBoldClick(String(children))}
              >
                {children}
              </strong>
            );
          }
          return <strong {...props}>{children}</strong>;
        },
        h1: ({ node, ...props }) => (
          <h1 className="text-2xl font-bold my-2" {...props} />
        ),
        h2: ({ node, ...props }) => (
          <h2 className="text-xl font-bold my-2" {...props} />
        ),
        h3: ({ node, ...props }) => (
          <h3 className="text-lg font-bold my-2" {...props} />
        ),
        h4: ({ node, ...props }) => (
          <h4 className="text-md font-bold my-2" {...props} />
        ),
        hr: ({ node, ...props }) => (
          <hr className="my-4 border-t border-gray-300" {...props} />
        ),
        ol: ({ node, ...props }) => (
          <ol className="list-decimal pl-4" {...props} />
        ),
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          return !inline && match ? (
            <SyntaxHighlighter
              style={oneDark}
              language={match[1]}
              PreTag="div"
              {...props}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        a({ node, children, href, ...props }) {
          if (!href) return children;
          let refNumber;
          if (references.has(href)) {
            refNumber = references.get(href);
          } else {
            refNumber = referenceCounter++;
            references.set(href, refNumber);
          }
          return (
            <FaviconLink
              href={href}
              refNumber={refNumber}
              className="inline-flex items-center gap-1 ml-1 hover:opacity-80 transition-opacity text-xs"
              {...props}
            >
              {children}
            </FaviconLink>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export default function ChatMessages({
  chatId,
  messages,
  isTyping,
  onSendMessage,
}) {
  const containerRef = useRef(null);
  const lastMessageRef = useRef(null);
  // A ref to store the index of the message that was auto-scrolled
  const lastScrolledMessageIndex = useRef(-1);

  useEffect(() => {
    const currentMessageIndex = messages.length - 1;
    // Only auto-scroll if a new message is added.
    if (
      containerRef.current &&
      lastMessageRef.current &&
      currentMessageIndex > lastScrolledMessageIndex.current
    ) {
      // Scroll to the last message
      containerRef.current.scrollTo({
        top: lastMessageRef.current.offsetTop,
        behavior: "smooth",
      });
      lastScrolledMessageIndex.current = currentMessageIndex;
    }
  }, [messages]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
      <div className="max-w-3xl mx-auto w-full space-y-3">
        {messages.map((msg, idx) => {
          const isLast = idx === messages.length - 1;
          let messageContent;
          if (msg.role === "user") {
            messageContent = (
              <div className="block px-4 py-2 rounded-lg shadow-sm break-words bg-green-100 text-gray-900 max-w-full sm:max-w-md md:max-w-lg lg:max-w-xl">
                {renderMarkdown(msg.content)}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {msg.sources.map((src, sidx) => (
                      <div key={sidx} className="flex items-center gap-1">
                        <FaviconLink
                          href={src.url}
                          refNumber={sidx + 1}
                          showPreview={true}
                          className="inline-flex items-center gap-2 hover:underline transition-opacity text-sm font-medium"
                        >
                          {src.title}
                        </FaviconLink>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          } else {
            messageContent = (
              <div className="relative group px-4 pt-2 pb-0 text-left text-gray-900 max-w-full sm:max-w-md md:max-w-lg lg:max-w-3xl">
                <div className="relative">
                  {renderMarkdown(msg.content, (boldText) =>
                    onSendMessage(`Tell me more about _${boldText}_`)
                  )}
                </div>
                {msg.sources && (
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {msg.sources.map((src, sidx) => (
                      <div key={sidx} className="flex flex-col items-center">
                        <FaviconLink
                          href={src.url}
                          refNumber={sidx + 1}
                          showPreview={true}
                          className="inline-flex items-center gap-2 hover:underline transition-opacity text-sm font-medium"
                        >
                          {src.title}
                        </FaviconLink>
                      </div>
                    ))}
                  </div>
                )}
                {msg.next_actions && (
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {msg.next_actions.map((action, aidx) => (
                      <button
                        key={aidx}
                        onClick={() => onSendMessage(action.label)}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all text-sm font-medium"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div
              key={idx}
              ref={isLast ? lastMessageRef : null}
              className={msg.role === "user" ? "flex flex-col items-end" : ""}
            >
              {messageContent}
            </div>
          );
        })}
        {isTyping &&
          (messages.filter((msg) => msg.role === "user").length === 0 ? (
            <div className="flex justify-center">
              <TypingIndicator initial={true} />
            </div>
          ) : (
            <div className="flex justify-start">
              <div className="bg-gray-200 text-gray-900 px-4 py-2 rounded-lg">
                <TypingIndicator initial={false} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
